import React, { useState } from 'react';
import {
  Calendar,
  CheckmarkFilled,
  CheckmarkOutline,
  Enterprise,
  Location,
  Phone,
  Tools,
  User,
  WarningAltFilled,
} from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import FullscreenLoading from 'components/FullscreenLoading/FullscreenLoading';
import LocalLanguageSelect, {
  useAnonymousLanguage,
} from 'components/LocalLanguageSelect/LocalLanguageSelect';
import {
  useGetExternalTrainingConfirmation,
  useRespondToExternalTraining,
} from 'data/external-training-confirmation-api-hooks';
import useObjectSearchParams from 'hooks/useObjectSearchParams';
import { parseAndFormatDate } from 'libs/dateFunctions';
import type { ExternalTrainingConfirmationViewModel } from 'model/ExternalTrainingConfirmation/types';
import { ExternalConfirmationState } from 'model/ExternalTrainingConfirmation/types';
import type { LocaleId } from 'model/locale';

// Anonymous external-training confirmation page (no login; authed by the signed token in the link).
// Design mirrors the mockup: https://mockups.ditio.app/external-training-confirm
// Uses the @ditioas/web-ui design tokens (sand/primary scales + btn-primary utility) — the standard
// Tailwind gray/blue/green palettes are not registered in this theme.

const DitioLogo = () => (
  <span className="flex items-center gap-2">
    <svg
      viewBox="0 0 1080 1080"
      className="h-6 w-6 rounded-md"
      role="img"
      aria-label="Ditio"
    >
      <rect width="1080" height="1080" rx="200" fill="#1B1B18" />
      <path
        d="M322 792.654H523.861C704.023 792.654 810 698.788 810 540.327C810 381.866 704.023 288 523.861 288H322V489.861H422.931V388.931H523.861C645.988 388.931 708.06 439.901 708.06 540.327C708.06 640.753 645.988 691.723 523.861 691.723H422.931V590.792H322V792.654ZM422.931 590.792H523.861V489.861H422.931V590.792Z"
        fill="#FDFDFC"
      />
    </svg>
    <span className="text-sand-1200 text-sm font-semibold">Ditio</span>
  </span>
);

const CardHeader = ({
  title,
  headerAction,
}: {
  title: string;
  headerAction?: React.ReactNode;
}) => (
  <div className="border-sand-200 flex shrink-0 items-center justify-between gap-3 border-b px-6 py-4">
    <DitioLogo />
    <span className="flex items-center gap-3">
      <span className="text-sand-900 text-xs font-medium">{title}</span>
      {headerAction}
    </span>
  </div>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sand-900 text-xs font-semibold tracking-wide uppercase">
    {children}
  </span>
);

const DetailRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}) => {
  if (!value) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 py-3">
      <span className="bg-sand-200 text-sand-1000 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-sand-900 block text-xs font-medium tracking-wide uppercase">
          {label}
        </span>
        <span className="text-sand-1200 mt-0.5 block font-semibold">
          {value}
        </span>
      </span>
    </div>
  );
};

const PageShell = ({
  title,
  headerAction,
  children,
}: {
  title: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="bg-sand-100 sm:bg-sand-200 min-h-dvh sm:flex sm:items-center sm:justify-center sm:p-8">
    <div className="bg-sand-100 relative flex min-h-dvh w-full flex-col sm:min-h-0 sm:max-w-md sm:rounded-3xl sm:bg-white sm:shadow-2xl sm:ring-1 sm:ring-black/5">
      <CardHeader title={title} headerAction={headerAction} />
      <div className="animate-in fade-in slide-in-from-bottom-3 flex flex-1 flex-col px-6 py-6 duration-300">
        {children}
      </div>
    </div>
  </div>
);

const ReviewState = ({
  view,
  language,
  isSubmitting,
  onRespond,
}: {
  view: ExternalTrainingConfirmationViewModel;
  language: LocaleId;
  isSubmitting: boolean;
  onRespond: (action: 'confirm' | 'decline') => void;
}) => {
  const { t } = useTranslation(undefined, { lng: language });

  const formattedDate = parseAndFormatDate({
    value: view.trainingDate,
    format: 'dd. MMMM yyyy HH:mm',
    localeId: language,
  });

  const confirmLabel = t('ExternalTrainingConfirm.Confirm');
  const declineLabel = t('ExternalTrainingConfirm.Decline');

  return (
    <>
      <div className="mb-5">
        <h1 className="text-sand-1200 text-2xl leading-snug font-bold">
          {t('ExternalTrainingConfirm.Question', {
            name: view.learnerName ?? '',
          })}
        </h1>
        <p className="text-sand-1100 mt-2 text-sm leading-relaxed">
          <span className="text-sand-1200 font-semibold">
            {view.learnerName}
            {view.companyName ? ` (${view.companyName})` : ''}
          </span>{' '}
          {t('ExternalTrainingConfirm.Intro')}
        </p>
      </div>

      {/* Equipment */}
      <div className="bg-primary-600 mb-4 rounded-2xl px-5 py-5 text-white shadow-md">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
            <Tools size={24} />
          </span>
          <span className="min-w-0">
            <span className="text-primary-100 block text-[11px] font-medium tracking-wider uppercase">
              {t('ExternalTrainingConfirm.Equipment')}
            </span>
            <span className="block truncate text-lg leading-tight font-bold">
              {view.equipmentName}
            </span>
            {view.additionalEquipment?.length > 0 && (
              <span className="text-primary-100 block truncate text-sm">
                {view.additionalEquipment.join(', ')}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Training details */}
      <SectionLabel>
        {t('ExternalTrainingConfirm.TrainingSection')}
      </SectionLabel>
      <div className="divide-sand-200 mt-1 divide-y">
        <DetailRow
          icon={<Calendar size={18} />}
          label={t('ExternalTrainingConfirm.TrainingDate')}
          value={formattedDate}
        />
        {view.showProject && (
          <DetailRow
            icon={<Location size={18} />}
            label={t('ExternalTrainingConfirm.Project')}
            value={view.projectName}
          />
        )}
      </div>

      {/* Tutor's own details */}
      <div className="mt-4 mb-1 flex items-baseline justify-between">
        <SectionLabel>{t('ExternalTrainingConfirm.YourDetails')}</SectionLabel>
        <span className="text-sand-900 text-xs">
          {t('ExternalTrainingConfirm.CheckCorrect')}
        </span>
      </div>
      <div className="divide-sand-200 border-sand-300 divide-y rounded-xl border px-4">
        <DetailRow
          icon={<User size={18} />}
          label={t('ExternalTrainingConfirm.Name')}
          value={view.tutorName}
        />
        <DetailRow
          icon={<Enterprise size={18} />}
          label={t('ExternalTrainingConfirm.Company')}
          value={view.tutorCompanyName}
        />
        <DetailRow
          icon={<Phone size={18} />}
          label={t('ExternalTrainingConfirm.Phone')}
          value={view.tutorPhoneNumber}
        />
      </div>

      {/* Disclaimer */}
      <p className="bg-sand-200/70 text-sand-1100 mt-4 rounded-xl px-4 py-3 text-sm leading-relaxed">
        {t('ExternalTrainingConfirm.Disclaimer')}{' '}
        <span className="text-sand-1200 font-semibold">{view.learnerName}</span>{' '}
        {t('ExternalTrainingConfirm.DisclaimerEnd')}
      </p>

      {/* Actions */}
      <div className="mt-5 flex flex-col gap-2.5">
        <button
          type="button"
          aria-label={confirmLabel}
          onClick={() => onRespond('confirm')}
          disabled={isSubmitting}
          className="btn-primary btn-sm btn-square relative w-full gap-2 disabled:opacity-50"
        >
          <CheckmarkOutline size={18} />
          {confirmLabel}
        </button>
        <button
          type="button"
          aria-label={declineLabel}
          onClick={() => onRespond('decline')}
          disabled={isSubmitting}
          className="text-sand-1000 w-full rounded-lg py-2.5 text-sm font-medium transition-colors hover:text-red-700 disabled:opacity-50"
        >
          {declineLabel}
        </button>
      </div>
    </>
  );
};

const ResultState = ({
  view,
  language,
}: {
  view: ExternalTrainingConfirmationViewModel;
  language: LocaleId;
}) => {
  const { t } = useTranslation(undefined, { lng: language });
  const isConfirmed = view.state === ExternalConfirmationState.Confirmed;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-4 pt-2 text-center">
        {isConfirmed ? (
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <CheckmarkFilled size={48} className="text-green-600" />
          </span>
        ) : (
          <span className="bg-sand-200 flex h-20 w-20 items-center justify-center rounded-full">
            <WarningAltFilled size={48} className="text-sand-900" />
          </span>
        )}
        <h1 className="text-sand-1200 text-2xl font-bold">
          {isConfirmed
            ? t('ExternalTrainingConfirm.ConfirmedTitle')
            : t('ExternalTrainingConfirm.DeclinedTitle')}
        </h1>
        <p className="text-sand-1100 text-sm leading-relaxed">
          {isConfirmed
            ? t('ExternalTrainingConfirm.ConfirmedBody', {
                name: view.learnerName ?? '',
              })
            : t('ExternalTrainingConfirm.DeclinedBody', {
                company: view.companyName ?? '',
              })}
        </p>
      </div>

      {isConfirmed && (
        <div className="border-sand-300 flex flex-col gap-4 rounded-xl border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sand-900 text-sm">
              {t('ExternalTrainingConfirm.Equipment')}
            </span>
            <span className="text-sand-1200 font-semibold">
              {view.equipmentName}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sand-900 text-sm">
              {t('ExternalTrainingConfirm.ReceivedTraining')}
            </span>
            <span className="text-sand-1200 font-semibold">
              {view.learnerName}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sand-900 text-sm">
              {t('ExternalTrainingConfirm.Date')}
            </span>
            <span className="text-sand-1200 font-semibold">
              {parseAndFormatDate({
                value: view.trainingDate,
                format: 'dd. MMMM yyyy HH:mm',
                localeId: language,
              })}
            </span>
          </div>
        </div>
      )}

      <p className="text-sand-900 text-center text-sm">
        {t('ExternalTrainingConfirm.CloseHint')}
      </p>
    </div>
  );
};

const ExternalTrainingConfirmation = () => {
  // Anonymous page: the tutor follows a link from an email and may not speak
  // the language the app defaults to, so it renders in its own.
  const [language, setLanguage] = useAnonymousLanguage();
  const { t } = useTranslation(undefined, { lng: language });
  const { trainingId } = useParams<{ trainingId?: string }>();
  const { xai, xat } = useObjectSearchParams<{ xai?: string; xat?: string }>();
  const respond = useRespondToExternalTraining();

  const { confirmation, isFetching } = useGetExternalTrainingConfirmation(
    trainingId,
    xai,
    xat
  );

  const [result, setResult] =
    useState<ExternalTrainingConfirmationViewModel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const view = result ?? confirmation;

  const handleRespond = async (
    action: 'confirm' | 'decline'
  ): Promise<void> => {
    if (!trainingId || !xai || !xat) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updated = await respond(trainingId, action, xai, xat);
      setResult(updated);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching || !view) {
    return <FullscreenLoading />;
  }

  const isPending = view.state === ExternalConfirmationState.Pending;

  return (
    <PageShell
      title={t('ExternalTrainingConfirm.HeaderTitle')}
      headerAction={
        <LocalLanguageSelect value={language} onChange={setLanguage} />
      }
    >
      {isPending ? (
        <ReviewState
          view={view}
          language={language}
          isSubmitting={isSubmitting}
          onRespond={handleRespond}
        />
      ) : (
        <ResultState view={view} language={language} />
      )}
    </PageShell>
  );
};

export default ExternalTrainingConfirmation;
